/**
 * HR Zone Bracket Math — client-side mirror of services/hr_zone_bands.py.
 *
 * Source of truth: services/hr_zone_bands.py (FIVE_ZONE_PCT,
 * LTHR_FIVE_ZONE_PCT, POLARIZED_3_PCT_LTHR, FRIEL_7_PCT_LTHR + the
 * compute_bands postconditions), delivered here as GENERATED DATA via
 * `./hrZoneTables.json` (written by scripts/export_hr_zone_tables.py).
 * FE-8 (Training Zones Drawer epic §4): the percentages are no longer
 * hand-copied — the checked-in JSON is re-rendered from the live Python
 * tables by tests/gate/test_hr_zone_bracket_math_parity.py on every CI
 * run, so ANY drift (or a stale JSON) fails CI.
 *
 * Why a client-side mirror? The HR Zones drawer renders a LIVE preview as
 * the user changes method / model / anchor values. Round-tripping every
 * keystroke through the backend would add 100–300 ms of latency. Instead
 * we consume the exported tables and recompute locally; on Save, the
 * backend recomputes from the same tables and the response shape matches
 * the preview byte-for-byte.
 *
 * Tier D-6 (2026-05-21) — extracted from the drawer refactor handoff;
 * FE-8 (2026-08-26) — tables moved onto the generated export, and the
 * backend `compute_bands` postconditions (per-band max-HR clamp,
 * contiguity, non-inversion) ported into `computeBands`.
 */

import zoneTables from './hrZoneTables.json';

// ──────────────────────────────────────────────────────────────────────────
// Generated tables — services/hr_zone_bands.py rendered as JSON.
// ──────────────────────────────────────────────────────────────────────────

interface JsonBracketBand {
  key: string;    // DB column stem: 'zone_1'..'zone_7' (positional)
  label: string;  // 'Z1'..'Z5', Friel 'Z5a'/'Z5b'/'Z5c'
  lo_pct: number;
  hi_pct: number;
}

interface ZoneTablesExport {
  five_zone_pct: JsonBracketBand[];
  lthr_five_zone_pct: JsonBracketBand[];
  polarized_3_pct_lthr: JsonBracketBand[];
  friel_7_pct_lthr: JsonBracketBand[];
  lthr_anchored_models: string[];
  model_zone_count: Record<string, number>;
  zone_display_names: Record<string, string[]>;
  zone_chart_colors: Record<string, string[]>;
}

const TABLES = zoneTables as unknown as ZoneTablesExport;

interface BracketBand {
  num: string;         // 'zone_1', 'zone_5a' (Friel sub-zones)
  name: string;        // 'Recovery', 'Aerobic', ... (SSOT display names)
  min_pct: number;
  max_pct: number;
}

/** Derive the legacy BracketBand shape from a generated table.
 *
 *  `num` keeps the historical FE vocabulary: Friel sub-zones are
 *  'zone_5a'/'zone_5b'/'zone_5c' (literal strings expected by the
 *  synthesizer card extractor — services/zones_model_prompt_block.py; the
 *  DB columns stay positional zone_5/6/7). Derived from the SSOT LABEL
 *  ('Z5a' → 'zone_5a') so the mapping cannot drift from the tables.
 *
 *  `name` comes from the SSOT `zone_display_names` — the ONLY legitimate
 *  source of zone labels (epic §1.4; K2/K3: a polarized athlete's top zone
 *  is 'High Intensity', never a borrowed 5-zone name). */
function toBracketBands(
  table: readonly JsonBracketBand[],
  names: readonly string[],
): readonly BracketBand[] {
  return table.map((b, i) => ({
    num: /^Z5[abc]$/i.test(b.label)
      ? `zone_5${b.label.slice(-1).toLowerCase()}`
      : b.key,
    name: names[i] ?? b.label,
    min_pct: b.lo_pct,
    max_pct: b.hi_pct,
  }));
}

/** Cedar 5-zone, Karvonen-style — % of HR Reserve (anchor = max_hr - resting_hr). */
export const CEDAR_5_HRR: readonly BracketBand[] = toBracketBands(
  TABLES.five_zone_pct, TABLES.zone_display_names.cedar_5,
);

/** Garmin 5-zone — % of Max HR (anchor = max_hr). Same percentages as
 *  CEDAR_5_HRR but applied to a different anchor (no HRR offset). */
export const GARMIN_5_PCT_MAX: readonly BracketBand[] = toBracketBands(
  TABLES.five_zone_pct, TABLES.zone_display_names.garmin_5,
);

/** LTHR-anchored 5-zone — Garmin/Firstbeat canonical brackets, % of LTHR
 *  (D3, ratified 2026-08-20: 81/89/94/99, top band capped at max HR).
 *  Backend: services/hr_zone_bands.LTHR_FIVE_ZONE_PCT. */
export const LTHR_5: readonly BracketBand[] = toBracketBands(
  TABLES.lthr_five_zone_pct, TABLES.zone_display_names.cedar_5,
);

/** Polarized 3-zone (Seiler/Stöggl) — % of LTHR. Z2 is the threshold band
 *  the athlete deliberately RATIONS (K2/K3) — its name is 'Threshold' and
 *  the top zone is 'High Intensity', from the SSOT display names. */
export const POLARIZED_3: readonly BracketBand[] = toBracketBands(
  TABLES.polarized_3_pct_lthr, TABLES.zone_display_names.polarized_3,
);

/** Friel 7-zone (Joe Friel periodization) — % of LTHR.
 *  IMPORTANT: zone-key vocabulary is `zone_5a` / `zone_5b` / `zone_5c`
 *  (see toBracketBands). Do not rewrite as zone_6/7. */
export const FRIEL_7: readonly BracketBand[] = toBracketBands(
  TABLES.friel_7_pct_lthr, TABLES.zone_display_names.friel_7,
);

// ──────────────────────────────────────────────────────────────────────────
// Public types
// ──────────────────────────────────────────────────────────────────────────

/** Computation method — how raw HR percentages turn into BPM bands. */
export type HRZoneMethod =
  | 'pct_max'        // % of Max HR (Garmin watch default)
  | 'karvonen'       // % of HR Reserve (Cedar default; needs resting HR)
  | 'pct_lthr'       // % of LTHR (Coggan/Friel; needs LTHR)
  | 'direct';        // user types BPM boundaries themselves

/** Display shape — how the bands are sliced (3 / 5 / 7). Orthogonal to method. */
export type HRZoneModel =
  | 'cedar_5'        // 5 bands, Cedar default labels
  | 'garmin_5'       // 5 bands, Garmin watch labels (math identical to cedar_5)
  | 'polarized_3'    // 3 bands (Seiler) — requires pct_lthr method
  | 'friel_7';       // 7 bands incl. Z5a/b/c (Friel) — requires pct_lthr method

/** A single computed band rendered in the live preview. */
export interface ComputedBand {
  num: string;       // 'zone_1' / 'zone_5a' (mirrors backend zone-key vocabulary)
  name: string;      // 'Recovery', 'SuperThreshold', ...
  lo: number;        // BPM lower bound (inclusive)
  hi: number;        // BPM upper bound (inclusive in display; backend stores as < hi)
}

/** Inputs to `computeBands`. Direct-entry mode carries `directBands`
 *  instead of anchor values. */
export interface ComputeBandsOpts {
  method: HRZoneMethod;
  model: HRZoneModel;
  maxHr: number;
  /** Required for `karvonen` method. */
  restingHr?: number;
  /** Required for `pct_lthr` method. */
  lthr?: number;
  /** Required for `direct` method; ignored otherwise. */
  directBands?: ComputedBand[];
}

// ──────────────────────────────────────────────────────────────────────────
// Dispatcher
// ──────────────────────────────────────────────────────────────────────────

/** Return the bracket table for a (method, model) combination, or null if
 *  the combination isn't supported by the backend. Used by the UI to grey
 *  out invalid picker combinations (e.g., pct_max + polarized_3). */
export function selectBrackets(
  method: HRZoneMethod,
  model: HRZoneModel,
): readonly BracketBand[] | null {
  if (method === 'direct') return null; // handled by caller
  if (method === 'pct_lthr') {
    if (model === 'polarized_3') return POLARIZED_3;
    if (model === 'friel_7') return FRIEL_7;
    return LTHR_5; // cedar_5 / garmin_5 share LTHR_5 boundaries
  }
  // pct_max / karvonen: only 5-band models supported by the backend.
  // polarized_3 / friel_7 with non-LTHR methods returns null so the picker
  // can grey it out instead of silently rendering a wrong table.
  if (model === 'polarized_3' || model === 'friel_7') return null;
  return method === 'pct_max' ? GARMIN_5_PCT_MAX : CEDAR_5_HRR;
}

/** Compute display bands for the live preview.
 *
 * Math rules (mirror backend `services/hr_zone_calculator.py`):
 *   - pct_max:   lo = round(max * min_pct),                 hi = round(max * max_pct)
 *   - karvonen:  lo = round(hrr * min_pct + rest),          hi = round(hrr * max_pct + rest)
 *   - pct_lthr:  lo = round(lthr * min_pct),                hi = round(lthr * max_pct)
 *
 * Rounding is half-up everywhere (JS `Math.round` ↔ backend `_round_half_up`,
 * i.e. `int(x + 0.5)`). The parity gate enforces JS == Python.
 *
 * The top zone's ceiling is anchored to `maxHr` for EVERY method — it
 * represents "above the previous boundary, up to max HR." LTHR-anchored
 * nominal tops (1.06×LTHR for LTHR_5, 1.10×LTHR for Polarized/Friel) sit
 * BELOW the user's max HR, so without this the model appears to "top out at
 * the lactate threshold instead of max HR." pct_max / karvonen tops already
 * equal maxHr, so anchoring is a no-op there.
 *
 * Returns `[]` when:
 *   - direct mode but no directBands
 *   - karvonen method but no restingHr
 *   - pct_lthr method but no lthr
 *   - (method, model) combination unsupported by backend (see selectBrackets)
 *
 * The UI uses an empty array as the "show inline 'we need X' hint" signal. */
export function computeBands(opts: ComputeBandsOpts): ComputedBand[] {
  const { method, model, maxHr, restingHr, lthr, directBands } = opts;

  if (method === 'direct') {
    return Array.isArray(directBands) ? directBands : [];
  }

  // Anchor guards — mirror of the backend's InsufficientAnchors raises
  // (services/hr_zone_bands.compute_bands). The UI reads [] as "show the
  // inline 'we need X' hint", the client-side analogue of the raise.
  //   - karvonen with resting <= 0: a stored 0 is a real value devices and
  //     legacy rows carry, and it produced bands byte-identical to %max —
  //     silently, under a "Karvonen" label.
  //   - karvonen with resting >= max: the reserve is non-positive; every
  //     band would collapse or invert.
  if (method === 'karvonen') {
    if (restingHr === undefined || restingHr === null || restingHr <= 0) {
      return [];
    }
    if (restingHr >= maxHr) {
      return [];
    }
  }
  if (method === 'pct_lthr' && (lthr === undefined || lthr === null)) {
    return [];
  }

  const brackets = selectBrackets(method, model);
  if (!brackets) return [];

  const lastIdx = brackets.length - 1;
  const bands = brackets.map((b, idx) => {
    let lo: number;
    let hi: number;

    // Rounding is half-up across every method, mirroring the backend's
    // `round_half_up` (services/hr_zone_bands.py). JS Math.round is
    // half-up for non-negative values; HR is always non-negative. The parity
    // gate (tests/gate/test_hr_zone_bracket_math_parity.py) enforces this.
    if (method === 'karvonen') {
      const hrr = maxHr - (restingHr as number);
      lo = Math.round(hrr * b.min_pct + (restingHr as number));
      hi = Math.round(hrr * b.max_pct + (restingHr as number));
    } else if (method === 'pct_lthr') {
      lo = Math.round((lthr as number) * b.min_pct);
      hi = Math.round((lthr as number) * b.max_pct);
    } else {
      // pct_max
      lo = Math.round(maxHr * b.min_pct);
      hi = Math.round(maxHr * b.max_pct);
    }

    // The top zone always terminates at the user's max HR — it represents
    // "everything above the previous boundary, up to max." For LTHR-anchored
    // models the nominal top bracket (1.10×LTHR) sits BELOW max HR for most
    // athletes, which previously left a dead band between it and max and made
    // the model appear to top out at the lactate threshold instead of max HR.
    // pct_max / karvonen tops already equal maxHr (no-op).
    //
    // NON-top bands: no band may OPEN above the athlete's ceiling. An LTHR
    // close to max HR pushes the upper %LTHR brackets past it — Friel's Z5c
    // opens at 1.07 × LTHR, so LTHR 172 / max 175 produced a band running
    // 184–175 (inverted) once the top was capped. Mirror of the clamp in
    // services/hr_zone_bands.compute_bands (finding Z14's structural half).
    if (idx === lastIdx) {
      hi = maxHr;
    } else {
      hi = Math.min(hi, maxHr);
    }
    lo = Math.min(lo, maxHr);

    return { num: b.num, name: b.name, lo, hi };
  });

  // Postconditions — line-for-line mirror of the backend's
  // `_enforce_contiguity` + `_enforce_non_inverted`
  // (services/hr_zone_bands.py). Contiguity first, then non-inversion, so a
  // repaired seam cannot re-introduce an inversion. A band squeezed to zero
  // width (lo === hi) is legal and classifies nothing — the honest outcome
  // for an athlete whose LTHR sits at their ceiling.
  for (let i = 0; i < bands.length - 1; i++) {
    if (bands[i].hi !== bands[i + 1].lo) {
      bands[i + 1].lo = bands[i].hi;
    }
  }
  for (const band of bands) {
    if (band.lo > band.hi) band.lo = band.hi;
  }
  if (bands.length > 0) {
    const top = bands[bands.length - 1];
    top.hi = maxHr;
    if (top.lo > maxHr) top.lo = maxHr;
  }
  return bands;
}

/** Whether the top zone of this (method, model) pair is "open-ended" — i.e.,
 *  the display should render `≥lo` instead of `lo–hi`. True for pct_lthr
 *  modes where the top bracket caps above max_pct=1.0 (zone 5 at 1.06, Friel
 *  Z5c at 1.10, Polarized Z3 at 1.10). */
export function isTopZoneOpenEnded(method: HRZoneMethod, model: HRZoneModel): boolean {
  if (method !== 'pct_lthr') return false;
  const brackets = selectBrackets(method, model);
  if (!brackets) return false;
  const top = brackets[brackets.length - 1];
  return top.max_pct > 1.0;
}

// ──────────────────────────────────────────────────────────────────────────
// Validation helpers (used by HRZonesAdvancedEntry + Save guards)
// ──────────────────────────────────────────────────────────────────────────

/** Minimum physiological max HR — guard against typos like `30 bpm`. */
export const PHYSIO_HR_MIN = 30;
/** Maximum physiological max HR — guard against typos like `350 bpm`. */
export const PHYSIO_HR_MAX = 250;

export interface DirectEntryValidation {
  ok: boolean;
  errors: string[];        // one human-readable error per failure
  perBandErrors: Record<string, string>; // keyed by zone.num for inline UI
}

/** Validate direct-entry bands per spec §"Edge cases" #8:
 *   - Z<n>.max === Z<n+1>.min (zones must abut, no gaps / overlaps)
 *   - Z<n>.min < Z<n>.max
 *   - Z1.min ≥ 30, Zlast.max ≤ 250 (physiological bounds) */
export function validateDirectBands(bands: ComputedBand[]): DirectEntryValidation {
  const errors: string[] = [];
  const perBandErrors: Record<string, string> = {};

  if (bands.length === 0) {
    errors.push('At least one zone band is required.');
    return { ok: false, errors, perBandErrors };
  }

  // lo < hi per band
  for (const b of bands) {
    if (b.lo >= b.hi) {
      const msg = `${b.num} lower bound (${b.lo}) must be less than upper bound (${b.hi}).`;
      errors.push(msg);
      perBandErrors[b.num] = msg;
    }
  }

  // Abutting check
  for (let i = 0; i < bands.length - 1; i++) {
    const cur = bands[i];
    const next = bands[i + 1];
    if (cur.hi !== next.lo) {
      const msg = `${cur.num} ends at ${cur.hi} but ${next.num} starts at ${next.lo} — zones must abut.`;
      errors.push(msg);
      perBandErrors[cur.num] = msg;
    }
  }

  // Physiological bounds on edges
  const first = bands[0];
  const last = bands[bands.length - 1];
  if (first.lo < PHYSIO_HR_MIN) {
    const msg = `${first.num} lower bound (${first.lo}) is below the physiological minimum (${PHYSIO_HR_MIN}).`;
    errors.push(msg);
    perBandErrors[first.num] = msg;
  }
  if (last.hi > PHYSIO_HR_MAX) {
    const msg = `${last.num} upper bound (${last.hi}) exceeds the physiological maximum (${PHYSIO_HR_MAX}).`;
    errors.push(msg);
    perBandErrors[last.num] = msg;
  }

  return { ok: errors.length === 0, errors, perBandErrors };
}

/** True when LTHR exceeds max HR — invalid combination that blocks Save
 *  (spec §"Edge cases" #1). UI surfaces the specific error string below
 *  the LTHR input. */
export function lthrExceedsMaxHr(lthr: number | undefined, maxHr: number | undefined): boolean {
  if (lthr === undefined || maxHr === undefined) return false;
  return lthr > maxHr;
}
